// ─────────────────────────────────────────────
// KLOTHIQ — Accounts & Cashbook React Hooks
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@klothiq/domain';
import type { CashbookResponse, CashbookTypeKey } from '@/utils/cashbookCalc';

export type Partner = Database['public']['Tables']['partners']['Row'];
export type ExpenseCategory = Database['public']['Tables']['expense_categories']['Row'];
export type ImportBatch = Database['public']['Tables']['cashbook_import_batches']['Row'];

export const ACCOUNTS_KEY = 'accounts';
export const CASHBOOK_KEY = 'cashbook';
export const ACCOUNTS_SUMMARY_KEY = 'accounts_summary';
export const PARTNERS_KEY = 'partners';
export const EXPENSE_CATEGORIES_KEY = 'expense_categories';
export const IMPORT_BATCHES_KEY = 'import_batches';

export interface CashbookFilters {
  from?: string | null;
  to?: string | null;
  types?: CashbookTypeKey[];
  account_id?: string | null;
  partner_id?: string | null;
  category_id?: string | null;
  search?: string | null;
  page?: number;
  page_size?: number;
}

export interface AccountsSummaryPartner {
  partner_id: string;
  name: string;
  share_percent: number;
  is_active: boolean;
  contributed: number;
  pct_of_capital: number;
  entries: number;
  last_date: string | null;
}

export interface AccountsSummaryCategory {
  category_id: string | null;
  name: string;
  amount: number;
  pct: number;
}

export interface AccountsSummaryTrendPoint {
  bucket: string;
  income: number;
  expense: number;
  balance: number;
}

export interface AccountsSummary {
  from: string;
  to: string;
  cash_in_hand: number;
  klothiq_bank_balance?: number;
  klothiq_bank_id?: string | null;
  sales_balance?: number;
  ledger_closing_all_time: number;
  capital_total: number;
  sales_collected: number;
  purchases_paid: number;
  refunds: number;
  expenses: number;
  adjustments: number;
  net_operating: number;
  receivables: number;
  payables: number;
  partners: AccountsSummaryPartner[];
  categories: AccountsSummaryCategory[];
  trend: {
    bucket: 'day' | 'month';
    series: AccountsSummaryTrendPoint[];
  };
}

/** Invalidate all accounts-related query caches */
export function invalidateAccounts(qc: QueryClient) {
  void refreshSaleConsumers(qc);
  qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY] });
  qc.invalidateQueries({ queryKey: [CASHBOOK_KEY] });
  qc.invalidateQueries({ queryKey: [ACCOUNTS_SUMMARY_KEY] });
  qc.invalidateQueries({ queryKey: [PARTNERS_KEY] });
  qc.invalidateQueries({ queryKey: [EXPENSE_CATEGORIES_KEY] });
  qc.invalidateQueries({ queryKey: [IMPORT_BATCHES_KEY] });
  qc.invalidateQueries({ queryKey: ['cash_accounts'] });
  qc.invalidateQueries({ queryKey: ['cash_transactions'] });
  qc.invalidateQueries({ queryKey: ['dashboard_kpis'] });
  qc.invalidateQueries({ queryKey: ['sales'] });
}

// ── Query: Cashbook entries ──────────────────────────────────
export function useCashbook(filters: CashbookFilters = {}) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [CASHBOOK_KEY, orgId, filters],
    queryFn: async (): Promise<CashbookResponse> => {
      const payload: Record<string, unknown> = {};
      if (filters.from) payload.from = filters.from;
      if (filters.to) payload.to = filters.to;
      if (filters.types && filters.types.length > 0) payload.types = filters.types;
      if (filters.account_id) payload.account_id = filters.account_id;
      if (filters.partner_id) payload.partner_id = filters.partner_id;
      if (filters.category_id) payload.category_id = filters.category_id;
      if (filters.search) payload.search = filters.search;
      if (filters.page) payload.page = filters.page;
      if (filters.page_size) payload.page_size = filters.page_size;

      const { data, error } = await supabase.rpc('get_cashbook', {
        p_filters: payload,
      });
      if (error) throw error;
      return (data as unknown) as CashbookResponse;
    },
    enabled: !!orgId,
    staleTime: 1000 * 30,
  });
}

// ── Query: Accounts Summary (Overview KPIs & Trends) ─────────
export function useAccountsSummary(from?: string | null, to?: string | null) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [ACCOUNTS_SUMMARY_KEY, orgId, from ?? null, to ?? null],
    queryFn: async (): Promise<AccountsSummary> => {
      const { data, error } = await supabase.rpc('get_accounts_summary', {
        p_from: from || null,
        p_to: to || null,
      });
      if (error) throw error;
      return (data as unknown) as AccountsSummary;
    },
    enabled: !!orgId,
    staleTime: 1000 * 30,
  });
}

// ── Query: Partners list ─────────────────────────────────────
export function usePartners(includeInactive = false) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [PARTNERS_KEY, orgId, includeInactive],
    queryFn: async (): Promise<Partner[]> => {
      let q = supabase
        .from('partners')
        .select('*')
        .eq('organization_id', orgId!)
        .order('name');
      if (!includeInactive) {
        q = q.eq('is_active', true);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data as Partner[]) ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 60,
  });
}

// ── Query: Expense Categories list ───────────────────────────
export function useExpenseCategories(includeInactive = false) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [EXPENSE_CATEGORIES_KEY, orgId, includeInactive],
    queryFn: async (): Promise<ExpenseCategory[]> => {
      let q = supabase
        .from('expense_categories')
        .select('*')
        .eq('organization_id', orgId!)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (!includeInactive) {
        q = q.eq('is_active', true);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data as ExpenseCategory[]) ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 60,
  });
}

// ── Query: Import batches ────────────────────────────────────
export function useImportBatches() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [IMPORT_BATCHES_KEY, orgId],
    queryFn: async (): Promise<ImportBatch[]> => {
      const { data, error } = await supabase
        .from('cashbook_import_batches')
        .select('*')
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as ImportBatch[]) ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 30,
  });
}

// ── Mutation: Upsert Partner (Owner/Admin) ────────────────────
export function useUpsertPartner() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      id?: string;
      name: string;
      phone?: string | null;
      share_percent: number;
      is_active?: boolean;
      notes?: string | null;
    }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization context');

      const payload = {
        organization_id: orgId,
        name: input.name.trim(),
        phone: input.phone ? input.phone.trim() : null,
        share_percent: input.share_percent,
        is_active: input.is_active ?? true,
        notes: input.notes ? input.notes.trim() : null,
        updated_at: new Date().toISOString(),
        updated_by: profile?.id,
      };

      if (input.id) {
        const { data, error } = await supabase
          .from('partners')
          .update(payload)
          .eq('id', input.id)
          .eq('organization_id', orgId)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('partners')
          .insert({
            ...payload,
            created_by: profile?.id,
          })
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
    },
  });
}

// ── Mutation: Upsert Expense Category (Owner/Admin) ──────────
export function useUpsertExpenseCategory() {
  const queryClient = useQueryClient();
  const { organization } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      id?: string;
      name: string;
      keywords?: string[];
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization context');

      const payload = {
        organization_id: orgId,
        name: input.name.trim(),
        keywords: input.keywords ?? [],
        sort_order: input.sort_order ?? 100,
        is_active: input.is_active ?? true,
        updated_at: new Date().toISOString(),
      };

      if (input.id) {
        const { data, error } = await supabase
          .from('expense_categories')
          .update(payload)
          .eq('id', input.id)
          .eq('organization_id', orgId)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('expense_categories')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
    },
  });
}

// ── Mutation: Record Capital Contribution ────────────────────
export function useRecordCapital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      partner_id: string;
      amount: number;
      date?: string;
      contribution_date?: string;
      payment_method?: string;
      notes?: string | null;
      account_id?: string;
    }) => {
      const dateVal = input.contribution_date || input.date || new Date().toISOString().split('T')[0];
      const { data, error } = await supabase.rpc('record_capital_contribution', {
        p_data: {
          partner_id: input.partner_id,
          amount: input.amount,
          contribution_date: dateVal,
          date: dateVal,
          payment_method: input.payment_method || 'bank_transfer',
          notes: input.notes || null,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
    },
  });
}

// ── Mutation: Record Expense (v2) ────────────────────────────
export function useRecordExpenseV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      account_id: string;
      amount: number;
      particular: string;
      date?: string;
      expense_category_id?: string | null;
      payment_method?: string;
      notes?: string | null;
    }) => {
      const dateVal = input.date || new Date().toISOString().split('T')[0];
      const { data, error } = await supabase.rpc('record_expense', {
        p_data: {
          account_id: input.account_id,
          amount: input.amount,
          particular: input.particular,
          expense_date: dateVal,
          transaction_date: dateVal,
          date: dateVal,
          expense_category_id: input.expense_category_id || null,
          payment_method: input.payment_method || 'cash',
          notes: input.notes || null,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
    },
  });
}

// ── Mutation: Record Cash Adjustment ─────────────────────────
export function useRecordAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      account_id: string;
      amount: number;
      direction: 'income' | 'expense';
      particular: string;
      date?: string;
      notes?: string | null;
    }) => {
      const dateVal = input.date || new Date().toISOString().split('T')[0];
      const signedAmount = input.direction === 'expense' ? -Math.abs(input.amount) : Math.abs(input.amount);
      const { data, error } = await supabase.rpc('record_cash_adjustment', {
        p_data: {
          account_id: input.account_id,
          amount: signedAmount,
          direction: input.direction,
          reason: input.particular,
          particular: input.particular,
          transaction_date: dateVal,
          date: dateVal,
          notes: input.notes || null,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
    },
  });
}

// ── Mutation: Import Cashbook Entries ────────────────────────
export function useImportCashbook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      batch: {
        file_name: string;
        file_hash: string;
        row_count: number;
        total_income: number;
        total_expense: number;
        cutoff_date: string | null;
        account_id: string;
      };
      rows: Array<{
        date: string;
        particular: string;
        kind: 'capital' | 'sale' | 'purchase' | 'expense';
        amount: number;
        partner_id?: string | null;
        expense_category_id?: string | null;
        notes?: string | null;
      }>;
    }) => {
      const { data, error } = await supabase.rpc('import_cashbook_entries', {
        p_batch: input.batch,
        p_rows: input.rows,
      });
      if (error) throw error;
      return data as {
        success: boolean;
        batch_id: string;
        inserted: number;
        total_income: number;
        total_expense: number;
      };
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
    },
  });
}

// ── Mutation: Void Import Batch ──────────────────────────────
export function useVoidImportBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { batch_id: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('void_import_batch', {
        p_batch_id: input.batch_id,
        p_reason: input.reason || null,
      });
      if (error) throw error;
      return data as { success: boolean; voided: number };
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
    },
  });
}

// ── Mutation: Update / Set KLOTHIQ Bank Balance (FR-022) ──────
export function useUpdateBankBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      new_balance: number;
      date?: string;
      notes?: string;
    }) => {
      const dateVal = input.date || new Date().toISOString().split('T')[0];
      const { data, error } = await supabase.rpc('update_klothiq_bank_balance', {
        p_data: {
          new_balance: input.new_balance,
          date: dateVal,
          transaction_date: dateVal,
          notes: input.notes?.trim() || null,
        },
      });
      if (error) throw error;
      return data as {
        success: boolean;
        account_id: string;
        old_balance: number;
        new_balance: number;
        diff: number;
        cash_txn_id?: string;
      };
    },
    onSuccess: () => {
      invalidateAccounts(queryClient);
      queryClient.invalidateQueries({ queryKey: ['cash_accounts'] });
      queryClient.invalidateQueries({ queryKey: ['cash_transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard_kpis'] });
    },
  });
}

